// Search.jsx
'use strict';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import SwipeableViews from 'react-swipeable-views';
import { virtualize } from 'react-swipeable-views-utils';

import SearchAll from '../container/SearchAll.js';
import SearchFlix from '../container/SearchFlix.js';
import SearchStory from '../container/SearchStory.js';
import SearchCreator from '../container/SearchCreator.js';
import HashtagIndex from '../container/HashtagIndex.js';
import Redirect from '../container/Redirect.js';
import SortButton from '../container/SortButton.js';

import { ButtonId } from '../resource/trackingConstants.js';

import {
  searchType,
  searchTypeArray,
  searchSwaggerCategoryPath,
} from '../resource/searchConstants.js';

import media from '../style/media.js';

const swaggerCategories = [
  searchSwaggerCategoryPath.ONLINE,
  searchSwaggerCategoryPath.NEW,
];
const VirtualizeSwipeableViews = virtualize(SwipeableViews);

class Search extends React.PureComponent {
  state = {
    currentCategory: swaggerCategories[0],
  };
  swipeableActions = null;
  nextTick = null;

  componentDidUpdate() {
    if (this.swipeableActions) {
      this.swipeableActions.updateHeight();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.nextTick);
  }

  getRedirectPathname = () => {
    const { type, category } = this.props;

    const typeToUpperCase = type?.toUpperCase();
    if (!searchType[typeToUpperCase]) {
      return `/search/${searchType.ALL}`;
    }
    const shouldFallbackSwaggerCategory =
      type === searchType.SWAGGER && !swaggerCategories.includes(category);
    if (shouldFallbackSwaggerCategory) {
      return `/search/${searchType.SWAGGER}/${swaggerCategories[0]}`;
    }
    return null;
  };
  handleTabChange = async (event, value) => {
    const {
      replace,
      location: { search: locationSearch },
    } = this.props;
    const { currentCategory } = this.state;
    const search = new URLSearchParams(locationSearch);
    ['sorts', 'filters', 'feed'].forEach(key => search.delete(key));
    const searchString = search.toString() ? `?${search.toString()}` : '';
    if (value === searchType.SWAGGER) {
      await replace(`/search/${value}/${currentCategory}${searchString}`);
    } else {
      await replace(`/search/${value}${searchString}`);
    }

    this.nextTick = setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    });
  };

  handleChangeIndex = index => {
    this.handleTabChange(null, searchTypeArray[index]);
  };

  slideRenderer = ({ index, key }) => {
    const {
      query,
      category: categoryPath,
      swaggerOnlineFeed,
      swaggerNewFeed,
      type,
    } = this.props;
    let renderItem = null;
    let category = null;
    switch (searchTypeArray[index]) {
      case searchType.ALL:
        renderItem = (
          <SearchAll
            key={key}
            isActive={type === searchType.ALL}
            type={searchType.ALL}
          />
        );
        break;

      case searchType.FLIX:
        renderItem = query ? (
          <SearchFlix
            key={key}
            isActive={type === searchType.FLIX}
            type={searchType.FLIX}
          />
        ) : (
          <HashtagIndex key={key} />
        );

        break;

      case searchType.SWAGGER:
        if (categoryPath === searchSwaggerCategoryPath.ONLINE)
          category = swaggerOnlineFeed;
        else if (categoryPath === searchSwaggerCategoryPath.NEW)
          category = swaggerNewFeed;
        renderItem = (
          <SearchCreator
            key={key}
            isActive={type === searchType.SWAGGER}
            type={searchType.SWAGGER}
            category={category}
          />
        );
        break;

      case searchType.STORY:
        renderItem = (
          <SearchStory
            key={key}
            isActive={type === searchType.STORY}
            type={searchType.STORY}
          />
        );
        break;

      default:
        break;
    }

    return renderItem;
  };

  render() {
    const {
      query,
      type,
      location: { search },
    } = this.props;

    const redirectPathname = this.getRedirectPathname();
    if (redirectPathname) {
      return <Redirect to={{ pathname: redirectPathname, search }} />;
    }

    const isCreator = searchType.SWAGGER === type;
    const isAll = searchType.ALL === type;
    return (
      <Body>
        {!!query && !isAll && (
          <ConditionsWrapper isCreator={isCreator}>
            <SortButton
              remoteConfigKeyName={isCreator ? 'SEARCH_USER' : 'SEARCH_MESSAGE'}
              baseFeedName={''}
              mixpanelButtonId={ButtonId.Search.ButtonSearchSorting}
              defaultItemI18nKey={'search_default_sorting'}
            />
          </ConditionsWrapper>
        )}
        <SwipeableViewsContainer
          resistance
          index={searchTypeArray.indexOf(type)}
          onChangeIndex={this.handleChangeIndex}
          slideRenderer={this.slideRenderer}
          slideCount={searchTypeArray.length}
          action={actions => {
            this.swipeableActions = actions;
          }}
        />
      </Body>
    );
  }
}

Search.propTypes = {
  query: PropTypes.string,
  category: PropTypes.string,
  type: PropTypes.string,
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
  replace: PropTypes.func,
  swaggerOnlineFeed: PropTypes.string,
  swaggerNewFeed: PropTypes.string,
};

Search.defaultProps = {
  query: '',
  category: '',
  type: searchType.ALL,
  location: {},
  replace: () => null,
  swaggerOnlineFeed: null,
  swaggerNewFeed: null,
};

const Body = styled.div``;

const SwipeableViewsContainer = styled(VirtualizeSwipeableViews)`
  & > .react-swipeable-view-container {
    & > div[aria-hidden='false'] {
      height: 100%;
    }
    & > div[aria-hidden='true'] {
      height: 100vh;
    }
  }
`;

const ConditionsWrapper = styled.div`
  padding: 0px 20px 20px;
  display: flex;
  justify-content: flex-end;
  ${media.mobile`
    padding: 0px 16px ${({ isCreator }) => (isCreator ? 20 : 12)}px;
  `};
`;

export default withTranslation()(Search);
